// CommunicationDetailDialog.js
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Pencil, AlertCircle, User, Link2, Clock, Mail, MessageSquare, Calendar, CheckCircle2, Building2, MapPin } from 'lucide-react';
import { useCommunicationDetail } from './hooks/useCommunicationDetail';
import { TaskDialog } from './TaskDialog';
import ActivityDialogFooter from './shared/ActivityDialogFooter';

export const CommunicationDetailDialog = ({ open, onOpenChange, communication, onSuccess, associations = [], users = [], usersMap = {}, contactEntries = [], tasks = [], horses = [], accounts = [], showAccountDropdown = false, onCommunicationUpdate, onOpenTaskDetail, canEditAccount }) => {
  const {
    isEditing, setIsEditing,
    saving,
    showTaskDialog, setShowTaskDialog,
    formData, setFormData,
    filteredAssociations,
    effectiveContactEntries,
    handleSave, handleDelete, cancelEdit,
    getContactDisplay,
    canEdit, linkedAssoc, assignedUser, contactEntry, linkedTasks,
    linkedHorse, linkedAccount,
  } = useCommunicationDetail(communication, { associations, users, contactEntries, tasks, horses, accounts, showAccountDropdown, onCommunicationUpdate, canEditAccount, onOpenChange, onSuccess });

  if (!communication) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="communication-detail-dialog">
        <DialogHeader>
          <DialogTitle>Communication Details</DialogTitle>
        </DialogHeader>

        {isEditing ? (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-comm-summary">Summary *</Label>
              <Input
                id="edit-comm-summary"
                value={formData.activity_name}
                onChange={(e) => setFormData({ ...formData, activity_name: e.target.value })}
                placeholder="e.g., Discussed upcoming sale"
              />
            </div>

            {/* Conditional Row Layout based on context */}
            {showAccountDropdown ? (
              /* Horse Context: Show Account (left) + Association (right) */
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Linked Account (optional)</Label>
                  <Select 
                    value={formData.linked_account_id || 'none'} 
                    onValueChange={(val) => setFormData({ ...formData, linked_account_id: val === 'none' ? '' : val, association_id: '' })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {accounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.system_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Linked Association (optional)</Label>
                  <Select 
                    value={formData.association_id || 'none'} 
                    onValueChange={(val) => setFormData({ ...formData, association_id: val === 'none' ? '' : val })}
                    disabled={!formData.linked_account_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.linked_account_id ? "Select association" : "Select account first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {filteredAssociations.map(assoc => (
                        <SelectItem key={assoc.id} value={assoc.id}>
                          {assoc.entityName || assoc.relationship?.replace('_', ' ') || assoc.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              /* Account Context: Show Association + Horse */
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Linked Association</Label>
                  <Select 
                    value={formData.association_id || 'none'} 
                    onValueChange={(val) => setFormData({ ...formData, association_id: val === 'none' ? '' : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select association" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {filteredAssociations.map(assoc => (
                        <SelectItem key={assoc.id} value={assoc.id}>
                          {assoc.entityName} - {assoc.relationship?.replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Linked Horse</Label>
                  <Select 
                    value={formData.horse_id || 'none'} 
                    onValueChange={(val) => setFormData({ ...formData, horse_id: val === 'none' ? '' : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select horse" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {horses.map(horse => (
                        <SelectItem key={horse.id} value={horse.id}>
                          {horse.system_name || horse.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Method</Label>
                <Select 
                  value={formData.communication_method} 
                  onValueChange={(val) => {
                    const newMethodTypes = {
                      telephone: 'phone', text: 'phone',
                      email_direct: 'email', email_newsletter: 'email',
                      mailer: 'address',
                      in_person: null, video_conference: null,
                    };
                    const allowedType = newMethodTypes[val];
                    const currentEntry = effectiveContactEntries.find(e => e.id === formData.contact_entry_id);
                    const keepEntry = allowedType === undefined
                      ? true
                      : allowedType !== null && currentEntry?.contact_type === allowedType;
                    setFormData({
                      ...formData,
                      communication_method: val,
                      contact_entry_id: keepEntry ? formData.contact_entry_id : '',
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_person">In Person</SelectItem>
                    <SelectItem value="telephone">Telephone</SelectItem>
                    <SelectItem value="email_direct">Email (Direct)</SelectItem>
                    <SelectItem value="email_newsletter">Email (Newsletter)</SelectItem>
                    <SelectItem value="video_conference">Video Conference</SelectItem>
                    <SelectItem value="text">Text Message</SelectItem>
                    <SelectItem value="mailer">Mailer</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-comm-date">Date</Label>
                <Input
                  id="edit-comm-date"
                  type="date"
                  value={formData.start_date_time}
                  onChange={(e) => setFormData({ ...formData, start_date_time: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Team Member</Label>
                <Select 
                  value={formData.assigned_to_user_id || 'none'} 
                  onValueChange={(val) => setFormData({ ...formData, assigned_to_user_id: val === 'none' ? '' : val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Contact Detail Used</Label>
                <Select 
                  value={formData.contact_entry_id || 'none'} 
                  onValueChange={(val) => setFormData({ ...formData, contact_entry_id: val === 'none' ? '' : val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {effectiveContactEntries.map(entry => (
                      <SelectItem key={entry.id} value={entry.id}>
                        {entry.contact_type === 'email' && entry.email_address}
                        {entry.contact_type === 'phone' && entry.phone_number}
                        {entry.contact_type === 'address' && `${entry.street}, ${entry.city}`}
                        {entry.nickname && ` (${entry.nickname})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-comm-notes">Notes</Label>
              <Textarea
                id="edit-comm-notes"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Key discussion points..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Linked Tasks (optional)</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowTaskDialog(true)}
                >
                  Create New
                </Button>
              </div>
              <div className="border rounded p-3 max-h-32 overflow-y-auto space-y-2">
                {tasks.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No tasks available</p>
                ) : (
                  tasks.slice(0, 10).map(task => (
                    <div key={task.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`edit-task-${task.id}`}
                        checked={(formData.linked_task_ids || []).includes(task.id)}
                        onChange={(e) => {
                          const currentIds = formData.linked_task_ids || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, linked_task_ids: [...currentIds, task.id] });
                          } else {
                            setFormData({ ...formData, linked_task_ids: currentIds.filter(id => id !== task.id) });
                          }
                        }}
                        className="rounded"
                      />
                      <Label htmlFor={`edit-task-${task.id}`} className="text-sm cursor-pointer flex-1">
                        {task.activity_name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit-comm-sensitive"
                checked={formData.is_sensitive}
                onChange={(e) => setFormData({ ...formData, is_sensitive: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="edit-comm-sensitive" className="text-sm cursor-pointer">
                Mark as sensitive
              </Label>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="p-4 bg-muted/30 rounded">
              {communication.is_sensitive && (
                <Badge variant="destructive" className="flex items-center gap-1 w-fit mb-2">
                  <AlertCircle className="w-3 h-3" />
                  Sensitive
                </Badge>
              )}
              
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold">{communication.activity_name}</h3>
                <Badge variant="secondary" className="capitalize ml-3">
                  {communication.communication_method?.replace('_', ' ') || 'Unknown'}
                </Badge>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Date:</span>
                  <span>{communication.start_date_time ? new Date(communication.start_date_time).toLocaleDateString() : 'Not set'}</span>
                </div>
                
                {linkedAccount && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Linked Account:</span>
                    <span className="font-medium">{linkedAccount.system_name}</span>
                  </div>
                )}

                {linkedHorse && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Linked Horse:</span>
                    <span className="font-medium">{linkedHorse.system_name || linkedHorse.name}</span>
                  </div>
                )}

                {assignedUser && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Team Member:</span>
                    <span>{assignedUser.name || `${assignedUser.first_name || ''} ${assignedUser.last_name || ''}`.trim() || assignedUser.email}</span>
                  </div>
                )}
                
                {contactEntry && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Contact Used:</span>
                    <span>{getContactDisplay(contactEntry)}</span>
                  </div>
                )}
                
                {linkedAssoc && (
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Linked Association:</span>
                    <span className="font-medium">{linkedAssoc.entityName}</span>
                    <Badge variant="secondary" className="text-xs">
                      {linkedAssoc.relationship?.replace('_', ' ')}
                    </Badge>
                  </div>
                )}
                
                {linkedTasks && linkedTasks.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Linked Tasks:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      {linkedTasks.map(task => (
                        <div key={task.id} className="flex items-center gap-2 cursor-pointer"
                          onClick={() => { if (onOpenTaskDetail) { onOpenTaskDetail(task); onOpenChange(false); } }}>
                          <span className="text-sm text-primary hover:underline">{task.activity_name}</span>
                          {task.task_status && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground capitalize">
                              {task.task_status.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {communication.description && (
              <div className="space-y-1 pt-2">
                <Label className="text-sm font-medium">Notes</Label>
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">{communication.description}</p>
              </div>
            )}

            <div className="text-xs text-muted-foreground pt-2 border-t">
              Created: {new Date(communication.created_at).toLocaleDateString()} by {usersMap[communication.created_by_id] || 'Unknown'}
            </div>
          </div>
        )}

        <ActivityDialogFooter
          isEditing={isEditing}
          saving={saving}
          onSave={handleSave}
          onCancel={cancelEdit}
          onDelete={handleDelete}
          onClose={() => onOpenChange(false)}
          onEdit={() => setIsEditing(true)}
          canEdit={canEdit}
          deleteTestId="delete-comm-btn"
          editTestId="edit-comm-btn"
        />
      </DialogContent>
      
      {/* Nested Task Dialog */}
      {showTaskDialog && (
        <TaskDialog
          open={showTaskDialog}
          onOpenChange={setShowTaskDialog}
          accountId={communication.crm_entity_id}
          associations={associations}
          users={users}
          communications={[]}
          contactEntries={contactEntries}
          onSuccess={() => {
            setShowTaskDialog(false);
            onSuccess?.(); // Refresh parent data
          }}
        />
      )}
    </Dialog>
  );
};

